import HooksModalV5Key from "app/constants/HooksModalV5Key";
import { translate } from "app/filters/i18nFilter";
import {
    createColumnFactoryList
} from "app/pages/sales-intelligence/common-components/search-results-table/utils/columns";
import { isUpgradeCellValuePresent } from "app/pages/sales-intelligence/common-components/table-cells";
import {
    ItemsCountCell
} from "app/pages/sales-intelligence/common-components/table-cells/components/ItemsCountCell/ItemsCountCell";
import CompanyAppsCell from "app/pages/sales-intelligence/modules/apps-search/components/cells/CompanyAppsCell";
import type {
    EnrichedLeadsSearchRow,
    LeadsSearchColumnListConfig,
    LeadsSearchRow,
} from "app/pages/sales-intelligence/modules/leads-search";
import { LinksCell } from "app/pages/sales-intelligence/modules/leads-search/components/cells/LinksCell/LinksCell";
import {
    createBaseColumnList
} from "app/pages/sales-intelligence/modules/leads-search/configuration/columns/columnList";
import { SIGNALS_COLUMN_WIDTH } from "app/pages/sales-intelligence/modules/leads-search/configuration/columns/const";
import {
    CompanyDomainsCellContainer
} from "app/pages/sales-intelligence/modules/leads-search/containers/search-results-table/CompanyDomainsCell";
import LeadCellContainer
    from "app/pages/sales-intelligence/modules/leads-search/containers/search-results-table/LeadCell/LeadCellContainer";
import {
    SiUpgradeCellContainer
} from "app/pages/sales-intelligence/modules/leads-search/containers/SiUpgradeCellContainer/SiUpgradeCellContainer";
import { TagCell } from "app/pages/sales-intelligence/modules/lists/components/cells/TagCell/TagCell";
import InCrmCellContainer from "app/pages/sales-intelligence/modules/lists/containers/cells/InCrmCell";
import type { TagColumnValue } from "app/pages/sales-intelligence/modules/lists/types/columns";
import { COLUMN_SIGNALS_NAME } from "app/pages/sales-intelligence/sub-modules/signals/constants";
import {
    SignalCellRightBarLogicContainer
} from "app/pages/sales-intelligence/sub-modules/signals/containers/cells/SignalCellRightBarLogicContainer";
import { getSignalsCountFromRow } from "app/pages/sales-intelligence/sub-modules/signals/utils/tableColumns";

import { tableColumnAdapters } from "../../../leads-search/services/column-adapters/tableColumnAdapters";
import { CompanyActionsCellContainer } from "../../components/cells/CompanyActionsCell/CompanyActionsCellContainer";
import { CompanyContactsCellContainer } from "../../components/cells/CompanyContactsCell/CompanyContactsCellContainer";

const createListsBaseColumnList = (): LeadsSearchColumnListConfig =>
    createColumnFactoryList([
        [
            "company_domains",
            tableColumnAdapters.createTableColumnAdapter({
                width: 212,
                dataIndex: "company_domains",
                sorter: false,
                render: (value: LeadsSearchRow["company_domains"], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SiUpgradeCellContainer
                            isUpgradeVisible={isUpgradeCellValuePresent(record.company)}
                            featureKey={HooksModalV5Key.SalesSolutionDefault}
                        >
                            <CompanyDomainsCellContainer
                                value={value}
                                record={record}
                            />
                        </SiUpgradeCellContainer>
                    );
                },
            }),
        ],
        [
            "company_apps",
            tableColumnAdapters.createTableColumnAdapter({
                width: 120,
                dataIndex: "company_apps",
                title: translate("si.leads-search.table.column.company-apps"),
                tooltipTitle: translate("si.leads-search.table.column.company-apps.tooltip"),
                render: (value: LeadsSearchRow["company_apps"]) => <CompanyAppsCell value={value} />,
            }),
        ],
        [
            "company",
            tableColumnAdapters.createTableColumnAdapter({
                width: 212,
                fixed: true,
                dataIndex: "company",
                tooltipTitle: translate("grow.lists.exist.table.tooltip.company"),
                render: (value: LeadsSearchRow["company"], record: EnrichedLeadsSearchRow) => (
                    <SiUpgradeCellContainer
                        isUpgradeVisible={isUpgradeCellValuePresent(value)}
                        featureKey={HooksModalV5Key.SalesSolutionDefault}
                    >
                        <LeadCellContainer
                            leadsType="companies"
                            record={record}
                            withWebsiteLink={false}
                        >
                            {value}
                        </LeadCellContainer>
                    </SiUpgradeCellContainer>
                ),
            }),
        ],
        [
            COLUMN_SIGNALS_NAME,
            tableColumnAdapters.createTableColumnAdapter({
                width: SIGNALS_COLUMN_WIDTH,
                dataIndex: COLUMN_SIGNALS_NAME,
                render: (_value: string[], record: EnrichedLeadsSearchRow) => {
                    return (
                        <SignalCellRightBarLogicContainer row={record}>
                            <ItemsCountCell value={getSignalsCountFromRow(record)} />
                        </SignalCellRightBarLogicContainer>
                    );
                },
            }),
        ],
        [
            "links",
            tableColumnAdapters.createTableColumnAdapter({
                width: 96,
                dataIndex: "links",
                sorter: false,
                render: (value: LeadsSearchRow["links"], record: EnrichedLeadsSearchRow) => (
                    <LinksCell
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
        [
            "company_contacts_count",
            tableColumnAdapters.createTableColumnAdapter({
                width: 132,
                dataIndex: "company_contacts_count",
                render: (value: number, record: EnrichedLeadsSearchRow) => (
                    <CompanyContactsCellContainer
                        contactCount={value}
                        record={record}
                    />
                ),
            }),
        ],
        [
            "actions",
            tableColumnAdapters.createTableColumnAdapter({
                width: 64,
                dataIndex: "actions",
                sorter: false,
                render: (_: never, record: EnrichedLeadsSearchRow) => <CompanyActionsCellContainer record={record} />,
            }),
        ],
        [
            "tag",
            tableColumnAdapters.createTableColumnAdapter({
                width: 200,
                dataIndex: "tag",
                render: (value: TagColumnValue, record: EnrichedLeadsSearchRow) => (
                    <TagCell
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
    ]);

export const createListsColumnList = (): LeadsSearchColumnListConfig =>
    createColumnFactoryList([
        ...createBaseColumnList(),
        ...createListsBaseColumnList(),

        [
            "salesforce_url",
            tableColumnAdapters.createTableColumnAdapter({
                width: 110,
                fixed: false,
                dataIndex: "salesforce_url",
                render: (value: string, record: EnrichedLeadsSearchRow) => (
                    <InCrmCellContainer
                        integrationProvider="salesforce"
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
        [
            "hubspot_url",
            tableColumnAdapters.createTableColumnAdapter({
                width: 110,
                fixed: false,
                dataIndex: "hubspot_url",
                render: (value: string, record: EnrichedLeadsSearchRow) => (
                    <InCrmCellContainer
                        integrationProvider="hubspot"
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
        [
            "dynamics_url",
            tableColumnAdapters.createTableColumnAdapter({
                width: 130,
                fixed: false,
                dataIndex: "dynamics_url",
                render: (value: string, record: EnrichedLeadsSearchRow) => (
                    <InCrmCellContainer
                        integrationProvider="dynamics"
                        value={value}
                        record={record}
                    />
                ),
            }),
        ],
    ]);

export const createCRMListsColumnList = (): LeadsSearchColumnListConfig =>
    createColumnFactoryList([...createBaseColumnList(), ...createListsBaseColumnList()]);
